import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Scan, Plus, BookOpen, ListChecks } from "@phosphor-icons/react";
import QRScanner from "@/components/QRScanner";
import QuranPicker from "@/components/QuranPicker";
import SantriSelect from "@/components/SantriSelect";
import BulkAttendance from "@/components/BulkAttendance";
import LaporanDivisi from "@/components/LaporanDivisi";
import { queueOp, flushQueue } from "@/lib/offlineSync";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const SESI = ["subuh", "pagi", "siang", "malam"];
const STATUS_HAL = ["hadir", "izin", "alfa", "sakit", "berhalangan"];

/**
 * Menu Tahfiz — restricted to Ustaz/Ustazah/Admin/Pimpinan.
 * Contains: Absensi Halaqah (4 sesi), Ziyadah, Manzil, Statistik per santri.
 */
export default function DivisiTahfiz() {
  const [tab, setTab] = useState("halaqah_massal");
  const [halaqahs, setHalaqahs] = useState([]);
  const [halForm, setHalForm] = useState({ santri: null, halaqah_id: "", sesi: "subuh", tanggal: new Date().toISOString().slice(0, 10), status: "hadir", keterangan: "" });
  const [halItems, setHalItems] = useState([]);
  const [scanOpen, setScanOpen] = useState(false);
  const [massalHalaqah, setMassalHalaqah] = useState("");
  const [massalSesi, setMassalSesi] = useState("subuh");
  const [massalTanggal, setMassalTanggal] = useState(new Date().toISOString().slice(0, 10));

  const [setoranTipe, setSetoranTipe] = useState("ziyadah"); // ziyadah | manzil
  const [setoranForm, setSetoranForm] = useState({
    santri: null, tanggal: new Date().toISOString().slice(0, 10), hari: "",
    halaman_awal: "", halaman_akhir: "",
    nilai_kelancaran: "", nilai_makhraj: "", nilai_tajwid: "", nilai_mad: "",
    nilai_ketepatan: "", nilai_kelengkapan: "", nilai_adab: "",
    keterangan: "",
  });
  const [quran, setQuran] = useState({});
  const [setoranItems, setSetoranItems] = useState([]);

  const [statSantriNis, setStatSantriNis] = useState("");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/master/halaqah").then((r) => setHalaqahs(r.data));
    loadHalaqah(); loadSetoran();
    // eslint-disable-next-line
  }, []);

  const loadHalaqah = async () => {
    try {
      const { data } = await api.get("/absensi-halaqah", { params: { tanggal: halForm.tanggal } });
      setHalItems(data);
    } catch (_) {}
  };
  const loadSetoran = async () => {
    try {
      const { data } = await api.get("/tahfiz", { params: { tipe: setoranTipe, limit: 200 } });
      setSetoranItems(data);
    } catch (_) {}
  };
  useEffect(() => { loadSetoran(); }, [setoranTipe]);  // eslint-disable-line

  // -------- Halaqah submit --------
  const submitHalaqah = async (e) => {
    e?.preventDefault?.();
    if (!halForm.nis || !halForm.halaqah_id) return toast.error("NIS dan halaqah wajib");
    try {
      if (navigator.onLine) { await api.post("/absensi-halaqah", halForm); toast.success("Absensi halaqah tercatat"); }
      else { await queueOp({ op: "create", path: "/absensi-halaqah", payload: halForm }); toast.success("Offline — antre"); }
      setHalForm({ ...halForm, nis: "", keterangan: "" });
      loadHalaqah(); flushQueue(api);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const onScannedHal = (text) => { setScanOpen(false); setHalForm((f) => ({ ...f, nis: text })); setTimeout(submitHalaqah, 150); };

  // -------- Setoran submit --------
  const submitSetoran = async (e) => {
    e.preventDefault();
    if (!setoranForm.nis || !quran.surah_no) return toast.error("NIS + pilihan Al-Qur'an wajib");
    const payload = {
      nis: setoranForm.nis, tipe: setoranTipe, tanggal: setoranForm.tanggal, hari: setoranForm.hari,
      surat: quran.surat, surah_no: quran.surah_no, juz: quran.juz,
      ayat_dari: quran.ayat_dari, ayat_sampai: quran.ayat_sampai,
      halaman: quran.halaman, halaman_awal: setoranForm.halaman_awal ? Number(setoranForm.halaman_awal) : undefined,
      halaman_akhir: setoranForm.halaman_akhir ? Number(setoranForm.halaman_akhir) : undefined,
      nilai_kelancaran: n(setoranForm.nilai_kelancaran), nilai_makhraj: n(setoranForm.nilai_makhraj),
      nilai_tajwid: n(setoranForm.nilai_tajwid), nilai_mad: n(setoranForm.nilai_mad),
      nilai_ketepatan: n(setoranForm.nilai_ketepatan), nilai_kelengkapan: n(setoranForm.nilai_kelengkapan),
      nilai_adab: n(setoranForm.nilai_adab),
      keterangan: setoranForm.keterangan,
    };
    try {
      if (navigator.onLine) { await api.post("/tahfiz", payload); toast.success(`${setoranTipe} tercatat — nilai auto-hitung`); }
      else { await queueOp({ op: "create", path: "/tahfiz", payload }); toast.success("Offline — antre"); }
      setSetoranForm({ ...setoranForm, nis: "", halaman_awal: "", halaman_akhir: "",
        nilai_kelancaran: "", nilai_makhraj: "", nilai_tajwid: "", nilai_mad: "",
        nilai_ketepatan: "", nilai_kelengkapan: "", nilai_adab: "", keterangan: "" });
      loadSetoran(); flushQueue(api);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  // -------- Statistik --------
  const loadStat = async () => {
    if (!statSantriNis) return;
    try {
      const s = await api.get(`/santri/by-nis/${encodeURIComponent(statSantriNis)}`);
      const st = await api.get(`/tahfiz-stats/${s.data.id}`);
      setStats({ santri: s.data, ...st.data });
    } catch (e) { toast.error("Santri tidak ditemukan"); }
  };

  return (
    <div className="space-y-5">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400">Divisi Tahfiz — Khusus Ustaz/Ustazah, Admin, Pimpinan</div>
        <h1 className="font-display text-3xl font-bold text-[#064E3B]">Tahfiz Halaqah</h1>
      </div>

      <div className="imsyaf-card p-2 flex gap-1 flex-wrap">
        {[
          ["halaqah_massal", "Absensi Massal"],
          ["halaqah", "Individu / QR"],
          ["ziyadah", "Ziyadah (Setoran Baru)"],
          ["manzil", "Manzil (Murajaah)"],
          ["statistik", "Statistik Santri"],
          ["laporan", "Laporan Divisi"],
        ].map(([k, l]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => { setTab(k); if (k === "ziyadah" || k === "manzil") setSetoranTipe(k); }}
            className={`px-4 py-2 rounded-md text-sm font-medium ${tab === k ? "bg-[#14532d] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "laporan" && (
        <LaporanDivisi
          divisiKey="tahfiz"
          modules={[
            { key: "tahfiz_halaqah", label: "Absensi Halaqah" },
            { key: "tahfiz_ziyadah", label: "Ziyadah (Setoran Baru)" },
            { key: "tahfiz_manzil", label: "Manzil (Murajaah)" },
            { key: "tahfiz_pelanggaran", label: "Pelanggaran Tahfiz" },
          ]}
        />
      )}

      {tab === "halaqah_massal" && (
        <div className="space-y-4">
          <div className="imsyaf-card p-5 grid md:grid-cols-3 gap-3">
            <div><label className="text-xs">Halaqah *</label>
              <select className="imsyaf-input" value={massalHalaqah} onChange={(e) => setMassalHalaqah(e.target.value)} data-testid="massal-hal-select">
                <option value="">Pilih halaqah…</option>
                {halaqahs.map((h) => <option key={h.id} value={h.id}>{h.nama}</option>)}
              </select></div>
            <div><label className="text-xs">Sesi *</label>
              <select className="imsyaf-input" value={massalSesi} onChange={(e) => setMassalSesi(e.target.value)} data-testid="massal-hal-sesi">
                {SESI.map((s) => <option key={s} value={s}>{"Halaqah " + s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select></div>
            <div><label className="text-xs">Tanggal</label>
              <input type="date" className="imsyaf-input" value={massalTanggal} onChange={(e) => setMassalTanggal(e.target.value)} /></div>
          </div>
          {!massalHalaqah ? (
            <div className="imsyaf-card p-8 text-center text-slate-400">Pilih Halaqah terlebih dahulu untuk memuat daftar santri.</div>
          ) : (
            <BulkAttendance
              endpoint="/absensi-halaqah"
              basePayload={{ halaqah_id: massalHalaqah, sesi: massalSesi, tanggal: massalTanggal }}
              statuses={["hadir", "alfa", "izin", "sakit", "berhalangan"]}
              izinKey="izin"
              femaleOnlyStatuses={["berhalangan"]}
              onDone={loadHalaqah}
            />
          )}
        </div>
      )}

      {tab === "halaqah" && (
        <>
          <form onSubmit={submitHalaqah} className="imsyaf-card p-5 grid md:grid-cols-6 gap-3">
            <div className="md:col-span-3"><label className="text-xs">Santri (cari nama / kamar) *</label>
              <SantriSelect value={halForm.santri} onChange={(s) => setHalForm({ ...halForm, santri: s })} testid="hal-santri" /></div>
            <div className="md:col-span-2"><label className="text-xs">Halaqah *</label>
              <select required className="imsyaf-input" value={halForm.halaqah_id} onChange={(e) => setHalForm({ ...halForm, halaqah_id: e.target.value })} data-testid="hal-select">
                <option value="">Pilih…</option>{halaqahs.map((h) => <option key={h.id} value={h.id}>{h.nama}</option>)}
              </select></div>
            <div><label className="text-xs">Sesi *</label>
              <select className="imsyaf-input" value={halForm.sesi} onChange={(e) => setHalForm({ ...halForm, sesi: e.target.value })} data-testid="hal-sesi">
                {SESI.map((s) => <option key={s} value={s}>Halaqah {s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
              </select></div>
            <div><label className="text-xs">Tanggal</label><input type="date" className="imsyaf-input" value={halForm.tanggal} onChange={(e) => setHalForm({ ...halForm, tanggal: e.target.value })} /></div>
            <div><label className="text-xs">Status</label>
              <select className="imsyaf-input" value={halForm.status} onChange={(e) => setHalForm({ ...halForm, status: e.target.value })}>
                {STATUS_HAL.map((s) => <option key={s} value={s}>{s}</option>)}
              </select></div>
            <div className="md:col-span-6"><label className="text-xs">Keterangan</label>
              <input className="imsyaf-input" value={halForm.keterangan} onChange={(e) => setHalForm({ ...halForm, keterangan: e.target.value })} /></div>
            <div className="md:col-span-6 flex flex-wrap gap-2">
              <button type="submit" className="imsyaf-btn-primary" data-testid="hal-save"><Plus size={16}/> Simpan</button>
              <button type="button" onClick={() => setScanOpen(true)} className="imsyaf-btn-outline"><Scan size={16}/> Scan QR/Barcode</button>
            </div>
          </form>
          {scanOpen && (
            <div className="imsyaf-card p-5">
              <QRScanner onScan={onScannedHal} onError={(m) => { setScanOpen(false); toast.error(m); }} />
              <div className="text-right mt-3"><button onClick={() => setScanOpen(false)} className="imsyaf-btn-outline">Tutup</button></div>
            </div>
          )}
          <div className="imsyaf-card overflow-hidden">
            <table className="w-full text-sm zebra">
              <thead className="bg-slate-50"><tr>
                <th className="text-left px-4 py-2">Tanggal</th><th className="text-left px-4 py-2">Santri</th>
                <th className="text-left px-4 py-2">Halaqah</th><th className="text-left px-4 py-2">Sesi</th>
                <th className="text-left px-4 py-2">Status</th><th className="text-left px-4 py-2">Ustaz</th>
              </tr></thead>
              <tbody>{halItems.map((a) => (
                <tr key={a.id} className="border-t border-slate-100">
                  <td className="px-4 py-2">{a.tanggal}</td>
                  <td className="px-4 py-2"><div className="font-medium">{a.santri_nama}</div><div className="text-xs font-mono text-slate-500">{a.santri_nis}</div></td>
                  <td className="px-4 py-2">{a.halaqah_nama}</td>
                  <td className="px-4 py-2">Halaqah {a.sesi}</td>
                  <td className="px-4 py-2"><span className={`imsyaf-badge ${a.status === "hadir" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-700"}`}>{a.status}</span></td>
                  <td className="px-4 py-2 text-xs text-slate-500">{a.ustaz_nama}</td>
                </tr>
              ))}
              {halItems.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400">Belum ada.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {(tab === "ziyadah" || tab === "manzil") && (
        <>
          <form onSubmit={submitSetoran} className="imsyaf-card p-5 space-y-4">
            <div className="flex items-center gap-3">
              <BookOpen size={22} weight="duotone" className="text-[#166534]" />
              <div className="font-display text-lg font-semibold text-[#064E3B]">
                {setoranTipe === "ziyadah" ? "Ziyadah (Setoran Hafalan Baru)" : "Manzil (Murajaah)"}
              </div>
            </div>
            <div className="grid md:grid-cols-4 gap-3">
              <div className="md:col-span-2"><label className="text-xs">Santri (cari nama / kamar) *</label>
                <SantriSelect value={setoranForm.santri} onChange={(s) => setSetoranForm({ ...setoranForm, santri: s })} testid="setoran-santri" /></div>
              <div><label className="text-xs">Tanggal</label>
                <input type="date" className="imsyaf-input" value={setoranForm.tanggal} onChange={(e) => setSetoranForm({ ...setoranForm, tanggal: e.target.value })} /></div>
              <div><label className="text-xs">Hari</label>
                <input className="imsyaf-input" value={setoranForm.hari} onChange={(e) => setSetoranForm({ ...setoranForm, hari: e.target.value })} placeholder="Senin/Selasa/…" /></div>
              <div className="md:col-span-4"><label className="text-xs">Halaman (auto: awal→akhir)</label>
                <div className="flex gap-1">
                  <input type="number" step="0.5" className="imsyaf-input" placeholder="Awal" value={setoranForm.halaman_awal} onChange={(e) => setSetoranForm({ ...setoranForm, halaman_awal: e.target.value })} />
                  <input type="number" step="0.5" className="imsyaf-input" placeholder="Akhir" value={setoranForm.halaman_akhir} onChange={(e) => setSetoranForm({ ...setoranForm, halaman_akhir: e.target.value })} />
                </div>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#166534]">Pilih Ayat (dari database Al-Qur'an)</label>
              <QuranPicker value={quran} onChange={setQuran} />
            </div>
            {setoranTipe === "ziyadah" && (
              <div>
                <div className="text-xs font-semibold text-[#166534] mb-2">Penilaian (7 komponen; bobot diatur di Pengaturan → Bobot Tahfiz)</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    ["nilai_kelancaran", "Kelancaran"], ["nilai_makhraj", "Makhraj"],
                    ["nilai_tajwid", "Tajwid"], ["nilai_mad", "Mad"],
                    ["nilai_ketepatan", "Ketepatan"], ["nilai_kelengkapan", "Kelengkapan"],
                    ["nilai_adab", "Adab"],
                  ].map(([k, l]) => (
                    <div key={k}><label className="text-xs">{l}</label>
                      <input type="number" min="0" max="100" className="imsyaf-input" value={setoranForm[k]} onChange={(e) => setSetoranForm({ ...setoranForm, [k]: e.target.value })} data-testid={`nilai-${k}`} /></div>
                  ))}
                </div>
              </div>
            )}
            <div><label className="text-xs">Keterangan</label>
              <input className="imsyaf-input" value={setoranForm.keterangan} onChange={(e) => setSetoranForm({ ...setoranForm, keterangan: e.target.value })} /></div>
            <button className="imsyaf-btn-primary" data-testid="setoran-save"><Plus size={16}/> Simpan {setoranTipe}</button>
          </form>

          <div className="imsyaf-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm zebra">
                <thead className="bg-slate-50"><tr>
                  <th className="text-left px-4 py-2">Tanggal</th><th className="text-left px-4 py-2">Santri</th>
                  <th className="text-left px-4 py-2">Juz</th><th className="text-left px-4 py-2">Surah</th>
                  <th className="text-left px-4 py-2">Ayat</th><th className="text-left px-4 py-2">Halaman</th>
                  {setoranTipe === "ziyadah" && <>
                    <th className="text-left px-4 py-2">Nilai Total</th><th className="text-left px-4 py-2">Predikat</th>
                  </>}
                  <th className="text-left px-4 py-2">Ustaz</th>
                </tr></thead>
                <tbody>{setoranItems.filter((s) => s.tipe === setoranTipe).map((s) => (
                  <tr key={s.id} className="border-t border-slate-100">
                    <td className="px-4 py-2">{s.tanggal}</td>
                    <td className="px-4 py-2"><div className="font-medium">{s.santri_nama}</div><div className="text-xs font-mono text-slate-500">{s.santri_nis}</div></td>
                    <td className="px-4 py-2">{s.juz || "-"}</td>
                    <td className="px-4 py-2">{s.surat}</td>
                    <td className="px-4 py-2">{s.ayat_dari}-{s.ayat_sampai}</td>
                    <td className="px-4 py-2">{s.halaman || "-"}</td>
                    {setoranTipe === "ziyadah" && <>
                      <td className="px-4 py-2 font-bold text-[#166534]">{s.nilai_total || "-"}</td>
                      <td className="px-4 py-2">{s.predikat || "-"}</td>
                    </>}
                    <td className="px-4 py-2 text-xs text-slate-500">{s.guru_nama}</td>
                  </tr>
                ))}
                {setoranItems.filter((s) => s.tipe === setoranTipe).length === 0 && <tr><td colSpan={9} className="text-center py-8 text-slate-400">Belum ada.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === "statistik" && (
        <>
          <div className="imsyaf-card p-5 flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[260px]"><label className="text-xs">Cari Santri (nama / kamar)</label>
              <SantriSelect
                value={statSantriNis ? { nis: statSantriNis, nama: stats?.santri?.nama || statSantriNis } : null}
                onChange={(s) => { setStatSantriNis(s?.nis || ""); if (!s) setStats(null); }}
                testid="stat-santri-select"
              />
            </div>
            <button className="imsyaf-btn-primary" onClick={loadStat} disabled={!statSantriNis} data-testid="stat-load">Muat Statistik</button>
          </div>
          {stats && (
            <>
              <div className="imsyaf-card p-5">
                <div className="text-xs uppercase text-slate-400">Perkembangan hafalan untuk</div>
                <div className="font-display text-2xl font-bold text-[#064E3B]">{stats.santri.nama}</div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {[
                  ["Total Juz", stats.total_juz],
                  ["Total Halaman", stats.total_halaman],
                  ["Total Ziyadah", stats.total_ziyadah],
                  ["Total Manzil", stats.total_manzil],
                  ["Nilai Rata-rata", stats.nilai_rata_rata ?? "-"],
                ].map(([l, v]) => (
                  <div key={l} className="imsyaf-card p-4">
                    <div className="text-xs uppercase text-slate-500">{l}</div>
                    <div className="font-display text-2xl font-bold text-[#064E3B]">{v}</div>
                  </div>
                ))}
              </div>
              <div className="imsyaf-card p-5">
                <div className="font-display text-lg font-semibold text-[#064E3B] mb-3">Grafik Perkembangan Nilai (per bulan)</div>
                <div className="h-64">
                  <ResponsiveContainer><LineChart data={stats.chart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="bulan" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="nilai_rata" stroke="#14532d" strokeWidth={2} />
                  </LineChart></ResponsiveContainer>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function n(v) { return v === "" || v === undefined ? undefined : Number(v); }
